import React, { useMemo, useState } from 'react';
import DateField from '../connectedForm/DateInput/DateInput';
import CheckboxInput from '../connectedForm/CheckboxInput/CheckboxInput';
import { ReportTemplate } from '../../graphql/entities';
import Dropdown from '../connectedForm/Dropdown';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import {
  AccountBalanceCriteria,
  AlertType,
  FilterOperation,
  IGenerateAgedDebtReportMutationResult,
  IGenerateAgedDebtReportMutationVariables,
  IReportParametersInput,
  IReportTemplate,
  IReportTemplateEdge,
  IReportTemplateInput,
  IReportTemplatesDropdownQueryResult,
  IReportTemplatesDropdownQueryVariables,
  ISystemPrinter,
  ISystemPrintersDropdownQueryResult,
  ISystemPrintersDropdownQueryVariables,
  Maybe,
  ReportOutputType,
} from '../../graphql/schema/p4types';
import { useMutation, useQuery } from '@apollo/client';
import GenerateAgedDebtReportMutation from '../../graphql/mutations/customer/GenerateAgedDebtReportMutation.graphql';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { ModalBottomButtonContainer } from './Styles';
import { SecondaryButton, SuccessButton } from '../connectedForm/Button';
import { serviceActions } from '../../state/modules/service';
import getClientMutationId from '../../utils/getClientMutationId';
import { IJobMeta, useBackgroundProcess } from '../../utils/BackgroundJobs';
import { BasicHeader } from '../../actions/BasicHeader';
import SystemPrintersQuery from '../../graphql/queries/system/SystemSystemPrinterListQuery.graphql';
import { useDispatch } from 'react-redux';
import { useActionProps } from '../../utils/useActionProps';
import { IEmailConfig } from './DocumentModal2';
import { useValueState } from '../../utils/hooks';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import FluentModal from './FluentModal';

const getPrintActionTitle = (outputType: Maybe<ReportOutputType>) => {
  switch (outputType) {
    case ReportOutputType.Pdf:
    case ReportOutputType.Docx:
    case ReportOutputType.Xlsx:
    case ReportOutputType.Csv:
      return 'Download';
    case ReportOutputType.Email:
      return 'Send';

    case ReportOutputType.LocalPrinter:
    case ReportOutputType.SystemPrinter:
      return 'Print';
    default:
      return 'Go';
  }
};

const AgedDebtCriteriaModal = ({ onClose, modalOpen }: { onClose: () => void; modalOpen: boolean }) => {
  const [templates, setTemplates] = useState<Array<Maybe<IReportTemplate>>>([]);
  const [outputType, setOutputType] = useState<Maybe<{ value: ReportOutputType; label: string }>>(null);
  const [systemPrinter, setSystemPrinter] = useState<Maybe<ISystemPrinter>>(null);
  const [template, setTemplate] = useState<Maybe<IReportTemplate>>(null);
  const [isRetrospective, setIsRetrospective] = useValueState(false);
  const [includeOverCreditLimitOnly, setIncludeOverCreditLimitOnly] = useValueState(false);
  const [includeCustomers, setIncludeCustomers] = useState<AccountBalanceCriteria>(AccountBalanceCriteria.Outstanding);
  const [baseDate, setBaseDate] = useState(null);
  const [emailConfig, setEmailConfig] = useState<Maybe<IEmailConfig>>(null);
  const dispatch = useDispatch();

  const title = 'Aged Debt Report';

  useQuery<IReportTemplatesDropdownQueryResult, IReportTemplatesDropdownQueryVariables>(ReportTemplate.dropdown.query, {
    variables: {
      filters: [
        {
          filterParameters: {
            field: 'reportType',
            value: 'AGED_DEBT_REPORT',
          },
          filterOperation: FilterOperation.Equals,
        },
        {
          filterParameters: {
            field: 'reportType',
            value: 'AGED_DEBT_TRANSACTION_REPORT',
          },
          filterOperation: FilterOperation.Equals,
        },
      ],
    },
    onCompleted: (data) => {
      const nodes = ((data.list?.edges || []) as Array<IReportTemplateEdge>).map((edge) => edge.node);
      setTemplates(nodes);
    },
  });
  const { data: systemPrintersData } = useQuery<
    ISystemPrintersDropdownQueryResult,
    ISystemPrintersDropdownQueryVariables
  >(SystemPrintersQuery);

  const [generateAgedDebtReport] = useMutation<
    IGenerateAgedDebtReportMutationResult,
    IGenerateAgedDebtReportMutationVariables
  >(GenerateAgedDebtReportMutation, {
    onCompleted: (data) => {
      if (data?.generateAgedDebtReport?.result) {
        runBackgroundProcess(data.generateAgedDebtReport.result as IJobMeta);
        onClose();
      }
    },
  });

  const openFile = (reportFileName: string) => {
    return window.open(`/api/resources/report/${reportFileName}`, '_blank');
  };

  const runBackgroundProcess = useBackgroundProcess({
    title: 'Preparing dynamic report',
    onComplete: (job) => {
      const alertTitle = `${title} sent`;
      if (outputType?.value === ReportOutputType.SystemPrinter) {
        dispatch(serviceActions.genericAlert(alertTitle, `${alertTitle} will be sent to printer`, AlertType.Success));
      } else if (outputType?.value === ReportOutputType.Email) {
        dispatch(serviceActions.genericAlert(alertTitle, `${alertTitle} will be sent via email`, AlertType.Success));
      } else {
        const reportWindow = openFile(job.properties.reportFile);
        if (reportWindow && outputType?.value === ReportOutputType.LocalPrinter) {
          reportWindow.print();
        }
      }
    },
  });

  const systemPrinters = useMemo(() => {
    return systemPrintersData?.list?.edges?.map((value) => value?.node) || [];
  }, [systemPrintersData]);

  const actionProps = useActionProps(`customers`);

  const outputSelection = [
    {
      value: ReportOutputType.LocalPrinter,
      label: 'Local Printer',
    },
    {
      value: ReportOutputType.SystemPrinter,
      label: 'System Printer',
    },
    {
      value: ReportOutputType.Csv,
      label: 'CSV File',
    },
    {
      value: ReportOutputType.Xlsx,
      label: 'XLSX Spreadsheet',
    },
  ];

  const onPrintClick = () => {
    if (!template) {
      dispatch(serviceActions.genericAlert('Error', 'Please select a template', AlertType.Warning));
      return;
    }

    if (!outputType) {
      dispatch(serviceActions.genericAlert('Error', 'Please select an output type', AlertType.Warning));
      return;
    }

    const reportParametersEmailConfig: Pick<IReportParametersInput, 'emailToList' | 'emailSubject'> = {};
    if (outputType.value === ReportOutputType.Email && emailConfig) {
      reportParametersEmailConfig.emailToList = emailConfig.emailTo.map((email) => email.email).toString();
      reportParametersEmailConfig.emailSubject = emailConfig.emailSubject;
    }

    const variables: IGenerateAgedDebtReportMutationVariables = {
      input: {
        agedDebtReportCriteria: {
          findAllCriteria: {
            filters: actionProps.filters,
          },
          customers: actionProps.customers,
          include: actionProps.include,
          retrospective: isRetrospective,
          includeOverCreditLimitOnly: includeOverCreditLimitOnly,
          includeCustomers: includeCustomers,
          baseDate: baseDate || undefined,
        },
        clientMutationId: getClientMutationId(),
        reportParameters: {
          template: template as IReportTemplateInput,
          outputType: outputType.value,
          printerId: (outputType.value === ReportOutputType.SystemPrinter && systemPrinter?.id) || undefined,
          ...reportParametersEmailConfig,
        },
      },
    };

    generateAgedDebtReport({ variables: normalizeVariables(variables) });
    onClose();
  };

  return (
    <FluentModal isOpen={modalOpen} styles={{ main: { minWidth: '600px', paddingBottom: '60px' } }}>
      <BasicHeader>{title}</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Dropdown
          options={templates}
          valueKey="id"
          labelKey="name"
          onChange={(value: IReportTemplate) => {
            setTemplate(value);
            setOutputType(getDefaultOutputType(value.reportTemplateFormat));
          }}
          label="Template"
          value={template}
          inputCols={2 / 3}
          labelCols={1 / 3}
        />
        <Dropdown
          dataid="outputType"
          options={outputSelection}
          label="Output"
          onChange={setOutputType}
          value={outputType}
          valueKey="value"
          labelKey="label"
          clearable={false}
          inputCols={2 / 3}
          labelCols={1 / 3}
        />
        {outputType?.value === ReportOutputType.SystemPrinter ? (
          <Dropdown
            options={systemPrinters}
            component={Dropdown}
            label="System Printer"
            onChange={setSystemPrinter}
            value={systemPrinter}
            valueKey="id"
            labelKey="printerName"
            inputCols={2 / 3}
            labelCols={1 / 3}
          />
        ) : null}
        <DateField
          inputCols={2 / 3}
          labelCols={1 / 3}
          label="Base date for ageing"
          portal={true}
          value={baseDate}
          onBlur={setBaseDate}
          onChange={setBaseDate}
          data-testid={'aged-debt-criteria-base-date'}
        />
        <CheckboxInput
          inputCols={2 / 3}
          labelCols={1 / 3}
          label="Retrospective"
          value={isRetrospective}
          onChange={setIsRetrospective}
          data-testid={'aged-debt-criteria-retrospective'}
        />
        <Dropdown
          options={fieldMetaData.AccountBalanceCriteria.enumValues}
          enum={true}
          valueKey="value"
          labelKey="label"
          label="Include customers with"
          rawLabel={true}
          value={includeCustomers}
          onChange={setIncludeCustomers}
          inputCols={2 / 3}
          labelCols={1 / 3}
        />
        <CheckboxInput
          inputCols={2 / 3}
          labelCols={1 / 3}
          label="Over credit limit only"
          value={includeOverCreditLimitOnly}
          onChange={setIncludeOverCreditLimitOnly}
          data-testid={'aged-debt-criteria-include-over-credit-limit-only'}
        />
      </div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={onClose}>Skip</SecondaryButton>
        <SuccessButton onClick={onPrintClick}>{getPrintActionTitle(outputType?.value)}</SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default AgedDebtCriteriaModal;
